/**
 * Motion path presets (W2-11 batch-tools; DESIGN.md §5.8, RPE Complex move CMOVE-01..06, E§4.12).
 *
 * Every preset IS an expression pair: `pathExpressions` writes the preset as x(t), y(t) (and r(t)) in the editor's
 * expression language, and the tool evaluates exactly that text. So the "equivalent expression" the sheet shows is
 * what was applied, and "Edit as expressions" turns a preset into an editable expression path with no change.
 * Variables (context 'motionPath'): `t` = path progress 0..1 after the time curve, `beat` = the sample's beat; `pi`,
 * every function of the language; RPE's `$t$` and `Pi` are accepted as written.
 *
 * Coordinates are RPE position units (X -675..675 across the screen, Y -450..450, up positive); angles of the
 * presets are degrees, counter-clockwise from +X.
 *  - line    from -> to
 *  - arc     around `center`, `radius`, from `fromDeg` to `toDeg`
 *  - circle  around `center`, `radius`, `turns` full turns starting at `startDeg` (negative turns: clockwise)
 *  - spiral  around `center`, radius r0 -> r1 over `turns` turns
 *  - figure8 around `center`, `w` wide and `h` tall (a sideways eight: x one wave, y two)
 *  - sine    from -> to with `waves` waves of amplitude `amp` across the direction of travel
 *  - expr    x(t), y(t) and optional r(t) as typed
 *
 * "Start from the line's position" (the sheet's default) moves the whole path so its first point is where the line
 * already is (`translatePreset`; motionPath.ts `anchoredPreset` measures the offset): the presets move their centre
 * or end points, expression paths get a constant added, so the equivalent expressions stay readable.
 */

import { Expr, evaluateProgram, motionPathScope } from '../expr/index';
import type { ExprError, ExprProgram } from '../expr/types';
import { mixSeed } from '../math/rng';
import type { TempoMap } from '../time/types';
import type { Result } from '../util/types';
import { numText } from './common';
import type { PathPreset } from './types';

export type PathAxis = 'x' | 'y' | 'r';

export interface PathExpressions {
  readonly x: string;
  readonly y: string;
  /** Rotation r(t) in degrees, or null when the path does not rotate the line. */
  readonly r: string | null;
}

export type PathPresetKind = PathPreset['kind'];

export const PATH_PRESET_KINDS: readonly PathPresetKind[] = [
  'line',
  'arc',
  'circle',
  'spiral',
  'figure8',
  'sine',
  'expr',
];

/** Starting parameters of each preset (RPE units, centred on the line's own position). */
export const PATH_PRESET_DEFAULTS: { readonly [K in PathPresetKind]: Extract<PathPreset, { kind: K }> } = {
  line: { kind: 'line', from: [-300, 0], to: [300, 0] },
  arc: { kind: 'arc', center: [0, 0], radius: 300, fromDeg: 0, toDeg: 180 },
  circle: { kind: 'circle', center: [0, 0], radius: 300, turns: 1, startDeg: 0 },
  spiral: { kind: 'spiral', center: [0, 0], r0: 50, r1: 300, turns: 2 },
  figure8: { kind: 'figure8', center: [0, 0], w: 600, h: 300 },
  sine: { kind: 'sine', from: [-500, 0], to: [500, 0], amp: 120, waves: 2 },
  expr: { kind: 'expr', x: '300 * cos(t * 2 * pi)', y: '300 * sin(t * 2 * pi)' },
};

/** `a + b * f` with the signs written naturally ("100 - 50 * f"), omitting zero terms. */
function affine(a: number, b: number, f: string): string {
  if (b === 0) return numText(a);
  const term = b === 1 ? f : b === -1 ? `-${f}` : `${numText(b)} * ${f}`;
  if (a === 0) return term;
  if (b < 0) return `${numText(a)} - ${b === -1 ? f : `${numText(-b)} * ${f}`}`;
  return `${numText(a)} + ${term}`;
}

/** Angle in radians of a degree expression `deg`, as text. */
function radians(deg: string): string {
  return `(${deg}) * pi / 180`;
}

/** Degrees linear in t: `a + (b - a) * t`, written compactly. */
function degreesOverT(a: number, b: number): string {
  return affine(a, b - a, 't');
}

/** x(t), y(t), r(t) of a preset in the expression language (see the file header). */
export function pathExpressions(p: PathPreset): PathExpressions {
  switch (p.kind) {
    case 'line':
      return {
        x: `lerp(${numText(p.from[0])}, ${numText(p.to[0])}, t)`,
        y: `lerp(${numText(p.from[1])}, ${numText(p.to[1])}, t)`,
        r: null,
      };
    case 'arc': {
      const a = radians(degreesOverT(p.fromDeg, p.toDeg));
      return {
        x: affine(p.center[0], p.radius, `cos(${a})`),
        y: affine(p.center[1], p.radius, `sin(${a})`),
        r: null,
      };
    }
    case 'circle': {
      const a = radians(degreesOverT(p.startDeg, p.startDeg + 360 * p.turns));
      return {
        x: affine(p.center[0], p.radius, `cos(${a})`),
        y: affine(p.center[1], p.radius, `sin(${a})`),
        r: null,
      };
    }
    case 'spiral': {
      const r = `lerp(${numText(p.r0)}, ${numText(p.r1)}, t)`;
      const a = radians(degreesOverT(0, 360 * p.turns));
      return {
        x: affine(p.center[0], 1, `${r} * cos(${a})`),
        y: affine(p.center[1], 1, `${r} * sin(${a})`),
        r: null,
      };
    }
    case 'figure8':
      return {
        x: affine(p.center[0], p.w / 2, 'sin(t * 2 * pi)'),
        y: affine(p.center[1], p.h / 2, 'sin(t * 4 * pi)'),
        r: null,
      };
    case 'sine': {
      const dx = p.to[0] - p.from[0];
      const dy = p.to[1] - p.from[1];
      const len = Math.hypot(dx, dy);
      // Unit normal of the direction of travel (+Y when from = to).
      const nx = len === 0 ? 0 : -dy / len;
      const ny = len === 0 ? 1 : dx / len;
      const wave = `sin(t * ${numText(2 * p.waves)} * pi)`;
      const along = (a: number, b: number): string => `lerp(${numText(a)}, ${numText(b)}, t)`;
      const across = (n: number): string => (n * p.amp === 0 ? '' : ` + ${numText(n * p.amp)} * ${wave}`);
      return {
        x: `${along(p.from[0], p.to[0])}${across(nx)}`,
        y: `${along(p.from[1], p.to[1])}${across(ny)}`,
        r: null,
      };
    }
    case 'expr': {
      const r = p.r?.trim() ?? '';
      return { x: p.x, y: p.y, r: r === '' ? null : r };
    }
  }
}

// ---------------------------------------------------------------------------------------------------------------
// Translation
// ---------------------------------------------------------------------------------------------------------------

/** A translation amount without float noise (`300 * cos(90deg)` is 1.8e-14, not 0). */
export function cleanOffset(v: number): number {
  if (!Number.isFinite(v) || Math.abs(v) < 1e-9) return 0;
  const r = Number(v.toPrecision(12));
  return r === 0 ? 0 : r;
}

function shiftPoint(p: readonly [number, number], dx: number, dy: number): readonly [number, number] {
  return [cleanOffset(p[0] + dx), cleanOffset(p[1] + dy)];
}

/** `(src) + d` written naturally ("(...) - 300"), or `src` itself for d = 0. */
export function shiftExpression(src: string, d: number): string {
  const v = cleanOffset(d);
  if (v === 0) return src;
  return v < 0 ? `(${src}) - ${numText(-v)}` : `(${src}) + ${numText(v)}`;
}

/**
 * The same path moved by (dx, dy), and its rotation by dr (only expression paths rotate). Presets keep their kind
 * (their centre or end points move), so their equivalent expressions stay readable.
 */
export function translatePreset(p: PathPreset, dx: number, dy: number, dr = 0): PathPreset {
  if (cleanOffset(dx) === 0 && cleanOffset(dy) === 0 && cleanOffset(dr) === 0) return p;
  switch (p.kind) {
    case 'line':
      return { ...p, from: shiftPoint(p.from, dx, dy), to: shiftPoint(p.to, dx, dy) };
    case 'sine':
      return { ...p, from: shiftPoint(p.from, dx, dy), to: shiftPoint(p.to, dx, dy) };
    case 'arc':
    case 'circle':
    case 'spiral':
    case 'figure8':
      return { ...p, center: shiftPoint(p.center, dx, dy) };
    case 'expr': {
      const r = p.r?.trim() ?? '';
      return {
        kind: 'expr',
        x: shiftExpression(p.x, dx),
        y: shiftExpression(p.y, dy),
        ...(r === '' ? {} : { r: shiftExpression(r, dr) }),
      };
    }
  }
}

/** Preset fields that only place the path, so "start from the line's position" makes them irrelevant. */
export function placementFields(kind: PathPresetKind): readonly string[] {
  switch (kind) {
    case 'arc':
    case 'circle':
    case 'spiral':
    case 'figure8':
      return ['center'];
    case 'line':
    case 'sine':
    case 'expr':
      return [];
  }
}

/**
 * What "start from the line's position" leaves of a preset's placement, for the sheet's hint: the centre makes no
 * difference ('centre': arc, circle, spiral, figure-8), only the step from `from` to `to` counts ('ends': line and
 * sine, whose fields stay editable because that step is the path), or nothing to say ('none': expressions get a
 * constant added, which the applied expressions show).
 */
export function anchorEffect(kind: PathPresetKind): 'centre' | 'ends' | 'none' {
  switch (kind) {
    case 'arc':
    case 'circle':
    case 'spiral':
    case 'figure8':
      return 'centre';
    case 'line':
    case 'sine':
      return 'ends';
    case 'expr':
      return 'none';
  }
}

/** A parsed path: point at progress t (after the time curve) of the sample at `beat` / `sec`. */
export interface CompiledPath {
  readonly expressions: PathExpressions;
  readonly rotates: boolean;
  /** null when an expression has no finite value there (division by zero, sqrt of a negative...). */
  at(t: number, beat: number, sec: number, index: number, count: number): PathPoint | null;
}

export interface PathPoint {
  readonly x: number;
  readonly y: number;
  /** Rotation in degrees (0 when the path does not rotate). */
  readonly r: number;
}

export interface PathCompileError {
  readonly axis: PathAxis;
  readonly error: ExprError;
}

function parseAxis(src: string, axis: PathAxis): Result<ExprProgram, PathCompileError> {
  const r = Expr.parse(src, 'motionPath');
  return r.ok ? r : { ok: false, error: { axis, error: r.error } };
}

/** Parses a preset's expressions (expression paths report the first axis with an error, with its range). */
export function compilePath(p: PathPreset, tempo?: TempoMap): Result<CompiledPath, PathCompileError> {
  const expressions = pathExpressions(p);
  const x = parseAxis(expressions.x, 'x');
  if (!x.ok) return x;
  const y = parseAxis(expressions.y, 'y');
  if (!y.ok) return y;
  let r: ExprProgram | null = null;
  if (expressions.r !== null) {
    const parsed = parseAxis(expressions.r, 'r');
    if (!parsed.ok) return parsed;
    r = parsed.value;
  }
  const px = x.value;
  const py = y.value;
  const pr = r;
  const evalOne = (prog: ExprProgram, scope: ReturnType<typeof motionPathScope>, seed: number): number | null => {
    // Random draws depend only on the sample index and the axis, never on how many samples were taken before.
    const res = evaluateProgram(prog, scope, Expr.rng(seed), tempo);
    return res.ok && Number.isFinite(res.value) ? res.value : null;
  };
  return {
    ok: true,
    value: {
      expressions,
      rotates: pr !== null,
      at(t, beat, sec, index, count) {
        const scope = motionPathScope({ progress: t, beat, sec, index, count });
        const vx = evalOne(px, scope, mixSeed(index, 1));
        const vy = evalOne(py, scope, mixSeed(index, 2));
        if (vx === null || vy === null) return null;
        if (pr === null) return { x: vx, y: vy, r: 0 };
        const vr = evalOne(pr, scope, mixSeed(index, 3));
        return vr === null ? null : { x: vx, y: vy, r: vr };
      },
    },
  };
}
